import api from "src/services/api";

/** Rutinas de limpieza por operario (TD-11191). Backend: api-central `rutina/`. */
const BASE = "rutina";

export type TipoTarea = "DIARIA" | "PROFUNDA";
export type Frecuencia = "DIARIA" | "SEMANAL" | "QUINCENAL" | "MENSUAL";
export type EstadoRutina = "VIGENTE" | "REEMPLAZADA" | "FINALIZADA" | "ANULADA";

export interface TareaCatalogo {
  id: number;
  tipo: TipoTarea;
  nombre: string;
}

export interface CatalogoTareas {
  diarias: TareaCatalogo[];
  profundas: TareaCatalogo[];
}

export interface TareaRutina {
  tarea_id: number;
  nombre: string;
  tipo: TipoTarea;
  frecuencia: Frecuencia;
  /** ISO: 1 = lunes ... 7 = domingo. */
  dias_semana: number[];
  /** Semana del mes (1..4): quincenal [1,3] o [2,4]; mensual una sola. */
  semanas_mes: number[];
  frecuencia_texto?: string;
  /** Solo en mi-rutina: le toca en la fecha consultada. */
  hoy?: boolean;
}

/** Línea de la rutina: franja horaria + sector + tareas, en orden de ejecución. */
export interface SectorRutina {
  sector_id?: number;
  /** La línea es de tareas diarias o de limpieza profunda, nunca ambas. */
  tipo?: "TAREAS" | "PROFUNDA";
  nombre: string;
  /** 'HH:MM' (null en rutinas cargadas antes de tener horario). */
  hora_desde?: string | null;
  hora_hasta?: string | null;
  /** Días ISO a los que aplica la línea ([] = todos, rutinas anteriores). */
  dias_semana?: number[];
  tareas: TareaRutina[];
  /** Solo en mi-rutina: la línea toca el día consultado. */
  hoy?: boolean;
}

export interface AsignacionRutina {
  asignacion_det_id: number;
  legajo: string;
  nombre: string;
}

export interface Rutina {
  rutina_id: number;
  numero: number | null;
  version: number;
  estado: EstadoRutina;
  ps_id: number;
  ps_codigo?: string | null;
  ps_nombre: string;
  baltic_nro?: string | null;
  observaciones?: string | null;
  emitido_nombre?: string | null;
  publicada_en?: string | null;
  /** Fin de esta versión (modificada, finalizada o anulada), quién y por qué. */
  baja_en?: string | null;
  baja_nombre?: string | null;
  baja_motivo?: string | null;
  /** Inicio de la rutina: publicación de su primera versión. */
  inicio_en?: string | null;
  /** Historial de versiones: quién y cuándo las cargó y les dio de baja. */
  versiones?: VersionRutina[];
  sectores: SectorRutina[];
  asignaciones?: AsignacionRutina[];
  aceptaciones?: { legajo: string; nombre: string; rol: string; aceptada_en: string }[];
}

export interface VersionRutina {
  rutina_id: number;
  version: number;
  estado: EstadoRutina;
  emitido_nombre?: string | null;
  publicada_en?: string | null;
  baja_nombre?: string | null;
  baja_en?: string | null;
  baja_motivo?: string | null;
}

export interface RutinaResumen extends Omit<Rutina, "sectores"> {
  operarios: string[];
  cant_sectores: number;
  cant_tareas: number;
}

/** Avance de las rutinas por PS (panel del Jefe de Operaciones). */
export interface ResumenPS {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
  fiscal: string;
  operarios: number;
  con_rutina: number;
  completas: number;
  parciales: number;
  desactualizadas: number;
}

export interface PuntoServicio {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
}

export interface OperarioPS {
  asignacion_det_id: number;
  /** Todos los cortes (filas de AsignacionDet) del operario en el PS: son una
   *  sola asignación partida (jornada partida). La rutina se vincula a todos. */
  asignacion_det_ids: number[];
  legajo: string;
  nombre: string;
  horario: { dia: string; entrada: string; salida: string }[];
  rutina_vigente: { rutina_id: number; numero: number | null; version: number } | null;
  /** Los cortes o días de la rutina ya no coinciden con su asignación. */
  rutina_desactualizada?: boolean;
  /** Cuánto del horario cubre su rutina (carga progresiva). */
  cobertura?: "COMPLETA" | "PARCIAL" | "SIN_CARGAR" | null;
}

export interface ContextoPS {
  ps: PuntoServicio;
  rutinas: RutinaResumen[];
  operarios: OperarioPS[];
  /** Relevamientos Baltic vigentes del PS (los mismos de la fiscalización). */
  baltics: { relevamiento: string; rutinas: string[] }[];
}

export interface RutinaPayload {
  baltic_nro: string;
  observaciones: string;
  /** Líneas: el backend las ordena por horario. */
  sectores: {
    nombre: string;
    hora_desde: string;
    hora_hasta: string;
    dias_semana: number[];
    /** Diarias: solo tarea_id. Profundas: + frecuencia, días y semanas. */
    tareas: ({ tarea_id: number } & Partial<Pick<TareaRutina, "frecuencia" | "dias_semana" | "semanas_mes">>)[];
  }[];
  /** Ids de AsignacionDet (todos los cortes de UN operario). */
  asignaciones: number[];
}

/** Rutina tal como la recibe el operario (sin datos internos del fiscal). */
export interface MiRutina {
  rutina_id: number;
  numero: number | null;
  version: number;
  ps_nombre: string;
  observaciones?: string | null;
  emitido_nombre?: string | null;
  publicada_en?: string | null;
  sectores: SectorRutina[];
  /** Día de la jornada (en turno nocturno, de madrugada es el día anterior). */
  fecha: string;
  rol: "TITULAR" | "REEMPLAZO";
  titular_nombre: string;
  aceptada_en: string | null;
  /** Horario del día consultado, ej. ["07:00-11:00", "14:00-18:00"]; vacío = no trabaja. */
  horario_hoy?: string[];
}

export const DIAS_SEMANA: { valor: number; corto: string; corto3: string; largo: string; clave: string }[] = [
  { valor: 1, corto: "Lu", corto3: "Lun", largo: "Lunes", clave: "lun" },
  { valor: 2, corto: "Ma", corto3: "Mar", largo: "Martes", clave: "mar" },
  { valor: 3, corto: "Mi", corto3: "Mié", largo: "Miércoles", clave: "mie" },
  { valor: 4, corto: "Ju", corto3: "Jue", largo: "Jueves", clave: "jue" },
  { valor: 5, corto: "Vi", corto3: "Vie", largo: "Viernes", clave: "vie" },
  { valor: 6, corto: "Sá", corto3: "Sáb", largo: "Sábado", clave: "sab" },
  { valor: 7, corto: "Do", corto3: "Dom", largo: "Domingo", clave: "dom" },
];

/** "Lun–Vie", "Lun, Mié", "Sáb" o "Todos los días". */
export const etiquetaDias = (dias: number[]): string => {
  const ordenados = Array.from(new Set(dias)).sort((a, b) => a - b);
  if (!ordenados.length || ordenados.length === 7) return "Todos los días";
  const corto = (d: number) => DIAS_SEMANA.find((x) => x.valor === d)?.corto3 || String(d);
  const tramos: number[][] = [];
  ordenados.forEach((d) => {
    const ultimo = tramos[tramos.length - 1];
    if (ultimo && d === ultimo[ultimo.length - 1] + 1) ultimo.push(d);
    else tramos.push([d]);
  });
  return tramos
    .map((t) => (t.length >= 3 ? `${corto(t[0])}–${corto(t[t.length - 1])}` : t.map(corto).join(", ")))
    .join(", ");
};

/**
 * Grupos de días a partir de los días de cada línea: los días que comparten
 * alguna línea quedan en el mismo grupo. Una línea "solo Lun, Jue" queda dentro
 * de su grupo Lun–Vie; días separados (sin líneas en común) forman grupos
 * propios. `universo`: días posibles (los que no aparecen en ninguna línea
 * quedan juntos en un grupo aparte). Misma regla que api-central
 * (componentes_dias).
 */
export const componentesDias = (listas: number[][], universo: number[] = [1, 2, 3, 4, 5, 6, 7]): number[][] => {
  const padre = new Map<number, number>(universo.map((d) => [d, d]));
  const raiz = (d: number): number => {
    let r = d;
    while (padre.get(r) !== r) r = padre.get(r)!;
    padre.set(d, r);
    return r;
  };
  listas.forEach((dias) => {
    const validos = dias.filter((d) => padre.has(d));
    validos.slice(1).forEach((d) => padre.set(raiz(d), raiz(validos[0])));
  });
  const usados = new Set(listas.flat());
  const grupos = new Map<number, number[]>();
  const sinLineas: number[] = [];
  universo.forEach((d) => {
    if (!usados.has(d)) return sinLineas.push(d);
    const r = raiz(d);
    grupos.set(r, [...(grupos.get(r) || []), d]);
  });
  const salida = Array.from(grupos.values()).map((g) => g.sort((a, b) => a - b));
  if (sinLineas.length) salida.push(sinLineas);
  return salida.sort((a, b) => a[0] - b[0]);
};

export { mensajeError } from "src/utils/errores";

const RutinaAPI = {
  /** gestiona: fiscal o jefe; jefe: ve el panel de avance de sus fiscales. */
  rol: () => api.get<{ datos: { gestiona: boolean; jefe: boolean } }>(`${BASE}/rol`),
  resumen: () => api.get<{ datos: ResumenPS[] }>(`${BASE}/resumen`),
  catalogo: () => api.get<{ datos: CatalogoTareas }>(`${BASE}/catalogo`),
  puntosServicio: (q: string) =>
    api.get<{ datos: PuntoServicio[] }>(`${BASE}/puntos-servicio?q=${encodeURIComponent(q)}`),
  contextoPS: (psId: number) => api.get<{ datos: ContextoPS }>(`${BASE}/ps/${psId}`),
  /** Crea la rutina directamente vigente (sin borradores). */
  crear: (payload: RutinaPayload & { ps_id: number }) => api.post<{ datos: Rutina }>(`${BASE}/`, payload),
  obtener: (id: number) => api.get<{ datos: Rutina }>(`${BASE}/${id}`),
  /** Guarda los cambios de una vigente como nueva versión. */
  modificar: (id: number, payload: RutinaPayload) =>
    api.post<{ datos: Rutina }>(`${BASE}/${id}/modificar`, payload),
  /** La rutina terminó: queda registrado su período de vigencia. */
  finalizar: (id: number, motivo?: string) => api.post(`${BASE}/${id}/finalizar`, { motivo }),
  /** Cargada por error. */
  anular: (id: number, motivo?: string) => api.post(`${BASE}/${id}/anular`, { motivo }),
  miRutina: (fecha?: string) =>
    api.get<{ datos: { rutinas: MiRutina[] } }>(
      `${BASE}/mi-rutina${fecha ? `?fecha=${fecha}` : ""}`
    ),
  aceptar: (id: number) => api.post(`${BASE}/${id}/aceptar`, {}),
};

export default RutinaAPI;
